import { Suspense, useRef, useEffect, useMemo } from "react";
import { Canvas, useFrame, useThree } from "@react-three/fiber";
import { Environment, Lightformer, Sparkles, ContactShadows } from "@react-three/drei";
import * as THREE from "three";

const CHROME = { color: "#f5f7fa", metalness: 0.9, roughness: 0.12 };
const BORE = { color: "#0b0b0c", metalness: 0.7, roughness: 0.55 };

const PIPE_R = 0.26;
const R_CC = 1.7;
const STRAIGHT = 3.6;
const MAX_BEND = Math.PI / 4;
const CYCLE = 16;

const ease = (u) => 0.5 - 0.5 * Math.cos(Math.PI * Math.min(Math.max(u, 0), 1));

const bendPhase = (t) => {
    const tau = (t % CYCLE) / CYCLE;
    let p;
    if (tau < 0.06) p = 0;
    else if (tau < 0.44) p = ease((tau - 0.06) / 0.38);
    else if (tau < 0.6) p = 1;
    else if (tau < 0.94) p = 1 - ease((tau - 0.6) / 0.34);
    else p = 0;
    return 0.001 + MAX_BEND * p;
};

const ChromePipe = () => {
    const group = useRef();
    const tube = useRef();
    const endCap = useRef();
    const tmp = useMemo(() => new THREE.Object3D(), []);
    const { size } = useThree();
    const isMobile = size.width < 768;

    useFrame((state, delta) => {
        const t = state.clock.elapsedTime;
        const theta = bendPhase(t);

        // Slow revolution on its axis + gentle float
        if (group.current) {
            group.current.rotation.y += delta * 0.28;
            group.current.position.y = (isMobile ? 0 : 0.15) + Math.sin(t * 0.5) * 0.09;
        }

        // Straight run + progressive arc up to 45°
        const pts = [];
        for (let x = -STRAIGHT; x <= 0.01; x += 0.45) pts.push(new THREE.Vector3(x, 0, 0));
        const steps = Math.max(2, Math.ceil(theta / 0.05));
        for (let i = 1; i <= steps; i++) {
            const phi = (theta * i) / steps;
            pts.push(new THREE.Vector3(R_CC * Math.sin(phi), R_CC * (1 - Math.cos(phi)), 0));
        }
        if (tube.current) {
            const curve = new THREE.CatmullRomCurve3(pts);
            const geo = new THREE.TubeGeometry(curve, 100, PIPE_R, 40, false);
            tube.current.geometry.dispose();
            tube.current.geometry = geo;
        }
        if (endCap.current) {
            const last = pts[pts.length - 1];
            endCap.current.position.copy(last);
            tmp.position.copy(last);
            tmp.lookAt(last.clone().add(new THREE.Vector3(Math.cos(theta), Math.sin(theta), 0)));
            endCap.current.quaternion.copy(tmp.quaternion);
        }
    });

    return (
        <group
            ref={group}
            position={isMobile ? [0.1, 0, 0] : [1.5, 0.15, 0]}
            rotation={[0, 0, 0.14]}
            scale={isMobile ? 0.95 : 1.38}
        >
            <mesh ref={tube}>
                <tubeGeometry />
                <meshStandardMaterial {...CHROME} />
            </mesh>
            {/* Open bore — straight end */}
            <mesh position={[-STRAIGHT - 0.001, 0, 0]} rotation={[0, -Math.PI / 2, 0]}>
                <ringGeometry args={[0.09, PIPE_R - 0.006, 40]} />
                <meshStandardMaterial {...BORE} side={THREE.DoubleSide} />
            </mesh>
            {/* Open bore — bent end */}
            <mesh ref={endCap}>
                <ringGeometry args={[0.09, PIPE_R - 0.006, 40]} />
                <meshStandardMaterial {...BORE} side={THREE.DoubleSide} />
            </mesh>
        </group>
    );
};

const Rig = () => {
    const pointer = useRef({ x: 0, y: 0 });

    useEffect(() => {
        const onMove = (e) => {
            pointer.current.x = (e.clientX / window.innerWidth) * 2 - 1;
            pointer.current.y = -(e.clientY / window.innerHeight) * 2 + 1;
        };
        window.addEventListener("mousemove", onMove);
        return () => window.removeEventListener("mousemove", onMove);
    }, []);

    useFrame((state) => {
        const t = state.clock.elapsedTime;
        const cam = state.camera;
        cam.position.x += (0.2 + Math.sin(t * 0.12) * 0.3 + pointer.current.x * 0.28 - cam.position.x) * 0.04;
        cam.position.y += (0.45 + Math.cos(t * 0.1) * 0.2 + pointer.current.y * 0.2 - cam.position.y) * 0.04;
        cam.lookAt(1.1, 0.35, 0);
    });
    return null;
};

export const TubularHero3D = () => (
    <Canvas
        data-testid="hero-3d-canvas"
        camera={{ position: [0.2, 0.45, 7.6], fov: 40 }}
        dpr={[1, 1.8]}
        gl={{ antialias: true, alpha: true, powerPreference: "high-performance" }}
        style={{ background: "transparent" }}
    >
        <Suspense fallback={null}>
            <ambientLight intensity={0.35} />
            {/* Hot specular key lights for chrome hotspots */}
            <pointLight position={[3, 2.5, 5]} intensity={3.2} color="#ffffff" />
            <pointLight position={[-4, 1, 4]} intensity={2.2} color="#f0f6ff" />
            <pointLight position={[0, -3, 2]} intensity={0.8} color="#00E07F" />
            <ChromePipe />
            {/* Fine metallic dust in the light */}
            <Sparkles count={40} position={[1.2, 0.4, 0]} scale={[7, 4.5, 4]} size={1.4} speed={0.18} opacity={0.4} color="#dfe6ee" />
            <Sparkles count={18} position={[1.2, 0.2, 0]} scale={[6, 4, 4]} size={2.2} speed={0.25} opacity={0.35} color="#00E07F" />
            <ContactShadows position={[1.4, -1.35, 0]} opacity={0.55} scale={12} blur={3} far={4.5} color="#000000" />
            {/* Studio strip lighting — long reflections that sweep along the pipe */}
            <Environment resolution={256}>
                <group rotation={[-Math.PI / 3, 0, 1]}>
                    {/* Large softbox behind the camera — the base silver reflection */}
                    <Lightformer form="rect" intensity={3.2} position={[0, 1, 12]} scale={[12, 6]} color="#dfe5ec" />
                    <Lightformer form="circle" intensity={14} position={[0, 4, -8]} scale={1.6} />
                    <Lightformer form="rect" intensity={9} position={[-6, 1.5, -1]} scale={[0.45, 6]} color="#ffffff" />
                    <Lightformer form="rect" intensity={7} position={[8, 2, 0]} scale={[0.5, 6]} color="#f4f8ff" />
                    <Lightformer form="rect" intensity={6} position={[0, 7, 2]} scale={[6, 0.45]} color="#ffffff" />
                    <Lightformer form="rect" intensity={3} position={[3, -4, 4]} scale={[4, 0.5]} color="#cfe0f5" />
                    <Lightformer intensity={1.6} position={[0, -5, 3]} scale={[3, 0.6]} color="#00E07F" />
                </group>
            </Environment>
            <Rig />
        </Suspense>
    </Canvas>
);
